import React, {useEffect, useLayoutEffect, useState} from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import Rating from '@material-ui/lab/Rating';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import lscache from 'lscache';
import ReactGA from 'react-ga4';

import {APICall} from '../../api/api';
import useStore from '../../store/store';
import usePersistentAuthStore from '../../store/persistentAuthStore';

const CAPTERRA_URL = 'https://reviews.capterra.com/search/';
const CAPTERRA_FAVICON_URL = 'https://www.google.com/s2/favicons?domain=capterra.com';
const HEADER_IMAGE =
  'https://setupad.com/wp-content/themes/setupad/assets/img/about/about-us-illustration.svg';
const DISMISS_MINUTES = 7 * 24 * 60;
const CAPTERRA_MIN_RATING = 4;

export const getReviewUsPopupLsKeys = accountId => ({
  dismissed: `review_us_popup_dismissed_${accountId}`,
  completed: `review_us_popup_completed_${accountId}`,
});

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 2,
    color: theme.name === 'darkTheme' ? '#fff' : 'inherit',
    backgroundColor:
      theme.name === 'darkTheme' ? 'rgb(26 24 29 / 85%)' : 'rgb(255 255 255 / 85%)',
  },
  container: {
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: 0,
    border:
      theme.name === 'darkTheme'
        ? '1px solid rgba(255,255,255,0.3)'
        : '1px solid #7979793b',
  },
  card: {
    overflow: 'hidden',
    borderRadius: 4,
    maxWidth: 480,
    margin: `${theme.spacing(6)}px auto 0`,
    boxShadow: 'none',
  },
  headerImage: {
    width: '100%',
    maxHeight: 180,
    objectFit: 'contain',
  },
  content: {
    padding: theme.spacing(3),
  },
  ratingRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  stars: {
    fontSize: '2.75rem',
    '& .MuiRating-iconFilled': {
      color: '#ffc107',
    },
    '& .MuiRating-iconHover': {
      color: '#ffb300',
    },
    '& .MuiRating-iconEmpty': {
      color: theme.palette.action.disabled,
    },
  },
  starsSmall: {
    '& .MuiRating-iconFilled': {
      color: '#ffc107',
    },
    '& .MuiRating-iconEmpty': {
      color: theme.palette.action.disabled,
    },
  },
  capterraFavicon: {
    backgroundColor: '#fff',
    borderRadius: 2,
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
  },
}));

const ReviewUsPopup = () => {
  const classes = useStyles();
  const {auth, updateAuthKey} = usePersistentAuthStore();
  const visiblePopup = useStore(state => state.visiblePopup);
  const setState = useStore(state => state.setState);
  const open = visiblePopup === 'reviewus';

  const [step, setStep] = useState('rating');
  const [rating, setRating] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('rating');
      setRating(null);
      setFeedbackText('');
      setStatus(null);
      setLoading(false);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;

    setState('disableTypingForAccountSelector', true);

    return () => setState('disableTypingForAccountSelector', false);
  }, [open, setState]);

  if (!auth || !open) return null;

  const lsKeys = getReviewUsPopupLsKeys(auth.accountId);

  const authHeaders = {
    Authorization: `Bearer ${auth.token}`,
    'Cache-Control': 'max-age=0, no-cache',
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };

  const closePopup = () => {
    setState('visiblePopup', null);
  };

  const handleClose = () => {
    if (step === 'capterra') {
      closePopup();
      return;
    }

    dismissForWeek();
  };

  const dismissForWeek = () => {
    lscache.set(lsKeys.dismissed, true, DISMISS_MINUTES);
    ReactGA.event({
      category: 'Review us popup',
      action: 'click',
      label: 'dismiss',
    });
    closePopup();
  };

  const persistReviewUsPopupCompletion = () => {
    lscache.set(lsKeys.completed, true, false);
  };

  const dismissReviewUsPopupSetting = async () => {
    persistReviewUsPopupCompletion();
    updateAuthKey('review_us_popup', null);

    await APICall({
      endpoint: 'dismissReviewUsPopup',
      options: {
        method: 'GET',
        headers: authHeaders,
      },
    });
  };

  const sendFeedback = async ({feedbackText: text = '', rating: selectedRating}) => {
    return APICall({
      endpoint: 'feedback',
      options: {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          feedbackText: text,
          pathname: window.location.pathname,
          rating: selectedRating,
        }),
      },
    });
  };

  const handleRatingSelect = async value => {
    setRating(value);
    ReactGA.event({
      category: 'Review us popup',
      action: 'click',
      label: `rating_${value}`,
    });

    if (value >= CAPTERRA_MIN_RATING) {
      setLoading(true);
      const query = await sendFeedback({rating: value});

      if (query.success) {
        await dismissReviewUsPopupSetting();
        setStep('capterra');
      } else {
        setRating(null);
        setStatus('error');
      }

      setLoading(false);
    } else {
      setStep('feedback');
    }
  };

  const handleCapterraClick = () => {
    ReactGA.event({
      category: 'Review us popup',
      action: 'click',
      label: 'capterra',
    });
    closePopup();
  };

  const submitFeedback = async () => {
    ReactGA.event({
      category: 'Review us popup',
      action: 'click',
      label: 'submit_feedback',
    });

    setLoading(true);
    const query = await sendFeedback({feedbackText, rating});
    setLoading(false);

    if (query.success) {
      setStatus('sent');
      setLoading(true);
      await dismissReviewUsPopupSetting();
      setLoading(false);
      closePopup();
    } else {
      setStatus('error');
    }
  };

  return (
    <Backdrop className={classes.backdrop} open={open}>
      <Container maxWidth="sm" className={classes.container}>
        <IconButton
          className={classes.closeButton}
          onClick={handleClose}
          color="inherit"
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>

        <Paper className={classes.card} elevation={0}>
          <img src={HEADER_IMAGE} alt="" className={classes.headerImage} />

          <div className={classes.content}>
            {step === 'rating' && (
              <>
                <Typography variant="h5" align="center" gutterBottom style={{fontWeight: 600}}>
                  How would you rate Setupad?
                </Typography>
                <Typography align="center" color="textSecondary" gutterBottom>
                  Tap a star — it only takes a second.
                </Typography>
                <Box className={classes.ratingRow}>
                  {loading ? (
                    <CircularProgress size={32} />
                  ) : (
                    <Rating
                      name="review-us-rating"
                      value={rating}
                      max={5}
                      size="large"
                      className={classes.stars}
                      onChange={(event, newValue) => {
                        if (newValue) handleRatingSelect(newValue);
                      }}
                    />
                  )}
                </Box>
                {status === 'error' && (
                  <Typography variant="caption" color="error" display="block" align="center" mt={2}>
                    Error, please try again.
                  </Typography>
                )}
              </>
            )}

            {step === 'capterra' && (
              <>
                <Typography variant="h5" align="center" gutterBottom style={{fontWeight: 600}}>
                  Thanks for your great rating!
                </Typography>
                <Typography align="center" color="textSecondary" paragraph>
                  Would you mind sharing your experience on Capterra? It only takes a minute and
                  helps other publishers discover us.
                </Typography>
                <Box display="flex" justifyContent="center" mt={2}>
                  <Button
                    component={Link}
                    href={CAPTERRA_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    startIcon={
                      <span className={classes.capterraFavicon}>
                        <img src={CAPTERRA_FAVICON_URL} alt="" width={16} height={16} />
                      </span>
                    }
                    onClick={handleCapterraClick}
                  >
                    Review us on Capterra
                  </Button>
                </Box>
              </>
            )}

            {step === 'feedback' && (
              <>
                <Typography variant="h5" align="center" gutterBottom style={{fontWeight: 600}}>
                  Tell us how we can do better
                </Typography>
                <Typography align="center" color="textSecondary" gutterBottom>
                  We read every message and use it to improve Setupad.
                </Typography>
                <TextField
                  onChange={event => {
                    setStatus(null);
                    setFeedbackText(event.target.value);
                  }}
                  value={feedbackText}
                  autoFocus
                  margin="dense"
                  label="Feedback"
                  variant="outlined"
                  multiline
                  rows={4}
                  fullWidth
                  size="small"
                  disabled={loading || status === 'sent'}
                />
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                  <Rating
                    name="review-us-rating-feedback"
                    value={rating}
                    readOnly
                    size="small"
                    className={classes.starsSmall}
                  />
                  <Button
                    disabled={loading || status === 'sent'}
                    variant="contained"
                    color="primary"
                    onClick={submitFeedback}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {loading ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
                {status === 'error' && (
                  <Typography variant="caption" color="error" display="block" align="center" mt={1}>
                    Error, please try again.
                  </Typography>
                )}
              </>
            )}
          </div>
        </Paper>
      </Container>
    </Backdrop>
  );
};

export default ReviewUsPopup;
